import React, { useState, useEffect } from "react";
import {
  Key,
  ExternalLink,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  Info,
} from "lucide-react";
import {
  getGeminiApiKey,
  saveGeminiApiKey,
  testGeminiApiKey,
} from "../../services/settingsService";

const AISettingsSection = ({ darkMode }) => {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const savedKey = getGeminiApiKey();
    if (savedKey) {
      setApiKey(savedKey);
    }
  }, []);

  const handleSave = () => {
    saveGeminiApiKey(apiKey.trim());
    setSaved(true);
    setTestResult(null);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTest = async () => {
    if (!apiKey.trim()) {
      setTestResult({
        success: false,
        message: "Please enter an API key first.",
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    const result = await testGeminiApiKey(apiKey.trim());
    setTestResult(result);
    setTesting(false);

    // Auto-save if test is successful
    if (result.success) {
      saveGeminiApiKey(apiKey.trim());
    }
  };

  const handleClear = () => {
    setApiKey("");
    saveGeminiApiKey("");
    setTestResult(null);
    setSaved(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-forest-100">
          AI Analysis Settings
        </h3>
      </div>

      {/* Info Box */}
      <div className="p-3 rounded-lg bg-forest-800/50 border border-forest-600">
        <p className="text-sm text-gold-200">
          AI-powered analysis features require a Google Gemini API key.
          <strong> The free tier is very generous</strong> - no credit card
          required!
        </p>
      </div>

      {/* API Key Input */}
      <div>
        <label className="block text-sm font-medium mb-2 text-forest-200">
          Google Gemini API Key
        </label>
        <input
          type={showKey ? "text" : "password"}
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            setTestResult(null);
            setSaved(false);
          }}
          placeholder="Enter your API key..."
          className="w-full px-3 py-2 rounded-lg border bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300 focus:outline-none focus:ring-2 focus:ring-gold-500"
        />
      </div>

      {/* Test Result */}
      {testResult && (
        <div
          className={`p-3 rounded-lg flex items-center gap-2 ${
            testResult.success
              ? "bg-forest-800/50 border border-forest-600"
              : "bg-ember-900/30 border border-ember-700"
          }`}
        >
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0 text-gold-400" />
          ) : (
            <XCircle className="w-5 h-5 flex-shrink-0 text-ember-500" />
          )}
          <span
            className={`text-sm ${
              testResult.success ? "text-gold-200" : "text-ember-200"
            }`}
          >
            {testResult.message}
          </span>
        </div>
      )}

      {/* Saved Indicator */}
      {saved && (
        <div className="p-3 rounded-lg flex items-center gap-2 bg-forest-800/50 border border-forest-600">
          <CheckCircle className="w-5 h-5 text-gold-400" />
          <span className="text-sm text-gold-200">Settings saved!</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowKey(!showKey)}
          className="py-2 px-3 rounded-lg font-medium flex items-center justify-center bg-forest-700 hover:bg-forest-600 text-forest-100 border border-forest-600"
          title={showKey ? "Hide key" : "Show key"}
        >
          {showKey ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
        {apiKey && (
          <button
            onClick={handleClear}
            className="py-2 px-3 rounded-lg font-medium flex items-center justify-center bg-forest-700 hover:bg-ember-900 text-forest-100 border border-forest-600"
            title="Clear key"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={handleTest}
          disabled={testing || !apiKey.trim()}
          className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
            testing || !apiKey.trim()
              ? "bg-forest-700 text-forest-300 cursor-not-allowed border border-forest-600"
              : "bg-forest-700 hover:bg-forest-600 text-forest-100 border border-forest-600"
          }`}
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Testing...
            </>
          ) : (
            "Test Connection"
          )}
        </button>
        <button
          onClick={handleSave}
          disabled={!apiKey.trim()}
          className={`flex-1 py-2 px-4 rounded-lg font-medium ${
            !apiKey.trim()
              ? "bg-forest-700 text-forest-300 cursor-not-allowed border border-forest-600"
              : "bg-forest-700 hover:bg-forest-600 text-forest-100 border border-forest-600"
          }`}
        >
          Save Key
        </button>
      </div>

      {/* Instructions Toggle */}
      <button
        onClick={() => setShowInstructions(!showInstructions)}
        className="w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-600 text-forest-100 border border-forest-600"
      >
        <Info className="w-4 h-4" />
        {showInstructions ? "Hide Instructions" : "How to Get a Free API Key"}
      </button>

      {/* Instructions */}
      {showInstructions && (
        <div className="p-4 rounded-lg bg-forest-700">
          <h4 className="font-semibold mb-3 text-forest-100">
            Get Your Free Gemini API Key
          </h4>
          <ol className="list-decimal list-inside space-y-2 text-sm text-forest-200">
            <li>Go to Google AI Studio</li>
            <li>Sign in with your Google account</li>
            <li>Click "Get API Key" in the left sidebar</li>
            <li>Click "Create API key"</li>
            <li>Copy the key and paste it above</li>
          </ol>

          <a
            href="https://aistudio.google.com/app/apikey"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-gold-600 hover:bg-gold-700 text-forest-900"
          >
            <ExternalLink className="w-4 h-4" />
            Open Google AI Studio
          </a>

          <div className="mt-4 p-3 rounded-lg bg-forest-600">
            <h5 className="font-medium mb-1 text-forest-100">
              Free Tier Includes:
            </h5>
            <ul className="text-sm space-y-1 text-forest-200">
              <li>• 60 requests per minute</li>
              <li>• 1 million tokens per day</li>
              <li>• No credit card required</li>
              <li>• Access to Gemini 2.0 Flash</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISettingsSection;
